import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from './api-error';

/**
 * The API answers `404 ROUTE_NOT_FOUND` for a route it does not have — a module whose
 * backend is not built yet — apart from the `<RESOURCE>_NOT_FOUND` of a missing record.
 */
export const ROUTE_NOT_FOUND = 'ROUTE_NOT_FOUND';

export function isMissingRoute(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.body.code === ROUTE_NOT_FOUND;
}

const MissingRouteContext = createContext<(() => void) | null>(null);

/**
 * A block of a page that depends on another module's routes (the deals or the
 * conversations of a customer): when one of its queries finds the route missing, the whole
 * block goes away instead of showing an error. Outside of it, `useHideOnMissingRoute` does
 * nothing and the block shows its errors as usual.
 */
export function HideOnMissingRoute({ children }: { children: ReactNode }) {
  const [missing, setMissing] = useState(false);
  const hide = useCallback(() => setMissing(true), []);
  if (missing) return null;
  return <MissingRouteContext.Provider value={hide}>{children}</MissingRouteContext.Provider>;
}

/** Reports the errors of a block's queries to the enclosing `HideOnMissingRoute`, if any. */
export function useHideOnMissingRoute(...errors: unknown[]): void {
  const hide = useContext(MissingRouteContext);
  const missing = errors.some(isMissingRoute);
  useEffect(() => {
    if (missing && hide) hide();
  }, [missing, hide]);
}
